class StickyAddToCart extends HTMLElement {
  constructor() {
    super();

    this.form = null;
    this.footer = null;
    this.content = null;
    this.formInView = true;
    this.footerInView = false;
    this.formObserver = null;
    this.footerObserver = null;
  }

  connectedCallback() {
    this.form = this.closest('[data-ref="product-form"]');
    this.footer = document.querySelector('[data-ref="footer"]');
    this.content = this.querySelector('.sticky-add-to-cart__content');

    // Le bouton sticky ne doit PAS ajouter au panier : son rôle ici est de
    // ramener l'internaute vers le choix de variante (taille / format), pas de
    // soumettre le formulaire. On intercepte le clic en phase de CAPTURE sur
    // l'élément sticky (ancêtre du bouton) : le gestionnaire s'exécute avant
    // que l'évènement n'atteigne le bouton ou ne remonte vers le <form>.
    //   - preventDefault()          → annule la soumission native (type=submit)
    //   - stopImmediatePropagation() → empêche tout écouteur d'ajout au panier
    //     (bouton, formulaire, add-to-cart-component) de se déclencher.
    // Attaché avant le garde ci-dessous pour rester actif même si le sticky
    // n'est pas dans un formulaire (cas dégradé).
    this.addEventListener('click', this.#onStickyClick.bind(this), true);

    if (!this.form || !this.content) return;

    // Réserve l'espace du sticky dans le flux pour éviter le changement de
    // hauteur du document quand le contenu passe en position: fixed. Sans cela,
    // le contenu sort du flux → la page raccourcit → le footer remonte dans le
    // viewport → le sticky se désactive → boucle infinie de clignotement.
    requestAnimationFrame(() => {
      this.style.minHeight = `${this.offsetHeight}px`;
      this.#initObservers();
    });
  }

  disconnectedCallback() {
    this.formObserver?.disconnect();
    this.footerObserver?.disconnect();
  }

  // Clic sur le bouton sticky → remonter vers le sélecteur de variante au lieu
  // d'ajouter au panier.
  // IMPORTANT : le MÊME bouton sert d'ajout au panier normal quand il est dans
  // le flux du formulaire (data-active="false") et de barre fixe en bas quand
  // on a défilé plus bas (data-active="true"). On n'intercepte donc QUE dans
  // l'état fixe : en position normale, l'ajout au panier reste inchangé.
  #onStickyClick(event) {
    if (this.dataset.active !== 'true') return;

    const trigger = event.target.closest(
      '[data-ref="add-to-cart-button"], button[name="add"], button[type="submit"]'
    );
    if (!trigger || !this.contains(trigger)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    // Cible de défilement, par ordre de préférence : le sélecteur de variante
    // du thème, sinon le formulaire produit (qui englobe aussi les sélecteurs
    // « format » injectés par l'app). Centré pour laisser voir le contexte.
    const target =
      document.querySelector('variant-picker') ||
      document.querySelector('.variant-picker') ||
      this.form ||
      document.querySelector('[data-ref="product-form"]');

    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  #initObservers() {
    this.formObserver = new IntersectionObserver(([entry]) => {
      this.formInView = entry.isIntersecting;
      this.#updateDisplay();
    });

    this.formObserver.observe(this.form);

    if (!this.footer) return;

    this.footerObserver = new IntersectionObserver(([entry]) => {
      this.footerInView = entry.isIntersecting;
      this.#updateDisplay();
    });

    this.footerObserver.observe(this.footer);
  }

  #updateDisplay() {
    const shouldBeActive = !this.formInView && !this.footerInView;
    const isActive = this.dataset.active === 'true';

    if (shouldBeActive === isActive) return;

    clearTimeout(this.closingTimeout);

    if (shouldBeActive) {
      delete this.dataset.closing;
      this.dataset.active = 'true';
      this.content.classList.add('color-scheme-1');
      return;
    }

    this.dataset.active = 'false';
    this.dataset.closing = 'true';

    this.closingTimeout = setTimeout(() => {
      delete this.dataset.closing;
      this.content.classList.remove('color-scheme-1');
    }, 300);
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
